/**
 * 矩形绘制状态 Store。
 * 仅管理矩形绘制过程中的实时参数流。
 */

import { defineStore } from 'pinia';
import {
  EntityInteractionEventType,
  SketchRectanglePropertyService,
  type RectangleParameters,
  type SketchCollectionObject,
} from '@puppycad/cad-core';
import { BehaviorSubject, type Observable, type Subscription } from 'rxjs';
import { useViewer } from '../useViewer';
import {
  observeViewerEntityInteraction,
  resolveViewerSelectedObjects,
} from './viewer-selection-helpers';

/**
 * 矩形参数快照。
 */
export type RectangleParametersSnapshot = RectangleParameters | null;

/**
 * 矩形绘制 Store 返回值接口。
 */
export interface RectangleSelectHookResult {
  /** 获取当前选中的矩形集合对象。 */
  getSelected: () => SketchCollectionObject | null;
  /** 获取矩形选中变化流。 */
  onChanged: () => Observable<SketchCollectionObject | null>;
  /** 获取矩形绘制实时变化的 Observable 流。 */
  onRectangleParametersChanged: () => Observable<RectangleParametersSnapshot>;
  /** 获取是否处于矩形绘制预览参数流。 */
  onDrawingPreviewChanged: () => Observable<boolean>;
  /** 显式开始监听矩形绘制参数事件。 */
  subscribe: () => void;
  /** 取消矩形绘制参数事件监听。 */
  unsubscribe: () => void;
  /** 显式同步当前矩形选择。 */
  syncCurrentSelection: () => void;
}

/**
 * 获取矩形绘制处理器派发的实时参数。
 */
//------ 矩形绘制状态 开始------
export const useGetSelectRectangle = defineStore('get-select-rectangle', (): RectangleSelectHookResult => {
  let selectedRectangle: SketchCollectionObject | null = null;
  let subscriptions: Subscription[] = [];
  let hasSelectionSubscribed = false;
  let hasDrawingSubscribed = false;
  let isDrawingPreview = false;
  /** cad-core 矩形属性服务，统一识别矩形集合并读取参数。 */
  const rectanglePropertyService = new SketchRectanglePropertyService();
  /** 矩形选中变化流。 */
  const rectangleChanged$ = new BehaviorSubject<SketchCollectionObject | null>(null);
  /** 统一的矩形绘制参数流。 */
  const rectangleParameters$ = new BehaviorSubject<RectangleParametersSnapshot>(null);
  /** 是否处于绘制预览状态。 */
  const drawingPreview$ = new BehaviorSubject<boolean>(false);
  const { getViewer } = useViewer();

  /**
   * 解析当前可用的草图绘制管理器。
   * @returns 草图绘制管理器；未就绪时返回 null。
   */
  function resolveRectangleDrawingParameters(): Observable<RectangleParametersSnapshot> | null {
    return getViewer()?.observeRectangleDrawingParameters?.() as Observable<RectangleParametersSnapshot> | null;
  }

  /**
   * 从实体选择管理器同步当前显式选中的矩形集合。
   *
   * 说明：矩形边线属于普通线条选择，不能通过 closedCollection 回退成矩形属性面板。
   */
  function syncFromManager(): void {
    selectedRectangle = rectanglePropertyService.resolveExplicitRectangleCollectionFromSelection(
      resolveViewerSelectedObjects(getViewer()),
    );
    rectangleChanged$.next(selectedRectangle);
    if (!isDrawingPreview) {
      rectangleParameters$.next(rectanglePropertyService.snapshotParameters(selectedRectangle));
    }
  }

  /**
   * 处理选择结算事件。
   */
  function handleSelectionUpdated(): void {
    syncFromManager();
  }

  /**
   * 处理选择清空事件。
   */
  function handleSelectionCleared(): void {
    selectedRectangle = null;
    rectangleChanged$.next(null);
    if (!isDrawingPreview) {
      rectangleParameters$.next(null);
    }
  }

  /**
   * 显式开始监听矩形绘制参数事件。
   */
  function subscribe(): void {
    const viewer = getViewer();
    const selectionUpdated$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionUpdated);
    const selectionCleared$ = observeViewerEntityInteraction(viewer, EntityInteractionEventType.SelectionCleared);
    const rectangleDrawingParameters$ = resolveRectangleDrawingParameters();
    if (hasSelectionSubscribed === false && selectionUpdated$ && selectionCleared$) {
      syncFromManager();
      subscriptions.push(
        selectionUpdated$.subscribe(handleSelectionUpdated),
        selectionCleared$.subscribe(handleSelectionCleared),
      );
      hasSelectionSubscribed = true;
    }
    if (hasDrawingSubscribed === false && rectangleDrawingParameters$) {
      subscriptions.push(
        rectangleDrawingParameters$.subscribe((params: RectangleParametersSnapshot) => {
          if (params) {
            isDrawingPreview = true;
            drawingPreview$.next(true);
            rectangleParameters$.next(params);
            return;
          }
          isDrawingPreview = false;
          drawingPreview$.next(false);
          rectangleParameters$.next(rectanglePropertyService.snapshotParameters(selectedRectangle));
        }),
      );
      hasDrawingSubscribed = true;
    }
  }

  /**
   * 取消矩形绘制参数事件监听。
   */
  function unsubscribe(): void {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
    subscriptions = [];
    hasSelectionSubscribed = false;
    hasDrawingSubscribed = false;
  }

  return {
    getSelected: (): SketchCollectionObject | null => selectedRectangle,
    onChanged: (): Observable<SketchCollectionObject | null> => rectangleChanged$.asObservable(),
    onRectangleParametersChanged: (): Observable<RectangleParametersSnapshot> => rectangleParameters$.asObservable(),
    onDrawingPreviewChanged: (): Observable<boolean> => drawingPreview$.asObservable(),
    subscribe,
    unsubscribe,
    syncCurrentSelection: syncFromManager,
  };
});
//------ 矩形绘制状态 结束------
